<template>
  <div v-if="pageCount > 1" class="pagination">
    <NuxtLink
      :to="previousPageLink"
      class="navigation-button"
      @click="$emit('change')"
    >
      <ArrowIcon class="arrow-icon arrow-icon-left" />
      <span class="visually-hidden" v-text="t('pagination.previous')" />
    </NuxtLink>
    <div class="pagination-pages">
      <Component
        :is="getTag(page)"
        v-for="page in getPaginationRange(filters.page.value, pageCount)"
        :key="String(page)"
        class="pagination-page t-pagination"
        :class="{ 'pagination-page--active': page === filters.page.value }"
        :to="page ? filters.getPageLink(page) : undefined"
        @click="$emit('change')"
      >
        {{ page || '...' }}
      </Component>
    </div>
    <NuxtLink
      :to="nextPageLink"
      class="navigation-button navigation-button--next"
      @click="$emit('change')"
    >
      <ArrowIcon class="arrow-icon arrow-icon-right" />
      <span class="t visually-hidden" v-text="t('pagination.next')" />
    </NuxtLink>
  </div>
</template>

<script lang="ts" setup>
import { range, sortBy } from 'lodash-es'

import ArrowIcon from '@/assets/icons/arrow-pagination.svg?component'
import type { ArticleFilters } from '@/composables/useArticleFilters'

const props = defineProps<{
  filters: ArticleFilters
  pageCount: number
}>()

defineEmits(['change'])

const nextPageLink = computed(() => {
  if (props.filters.page.value < props.pageCount)
    return props.filters.nextPageLink.value
})

const previousPageLink = computed(() => {
  if (props.filters.page.value > 1) return props.filters.previousPageLink.value
})

const getTag = (page: number | null) =>
  page ? resolveComponent('NuxtLink') : 'span'

const getPaginationRange = (current: number, final: number) => {
  if (final <= 5) {
    return range(1, final + 1)
  }

  // We always include the first two pages, last two pages, and
  // two pages either side of the current page.
  const included = new Set([1, current - 1, current, current + 1, final])

  // If the break would only exclude a single page number then we
  // may as well include the page number instead of the break.
  if (current <= 4) {
    included.add(2)
    included.add(3)
  }
  if (current >= final - 3) {
    included.add(final - 1)
    included.add(final - 2)
  }

  // Now sort the page numbers and drop anything outside the limits.
  const includedArray: (number | null)[] = sortBy(Array.from(included)).filter(
    idx => idx > 0 && idx <= final
  )

  // Finally insert any `...` breaks
  if (current > 4) {
    includedArray.splice(1, 0, null)
  }
  if (current < final - 3) {
    includedArray.splice(-1, 0, null)
  }
  return includedArray
}

const { t } = useI18n()
</script>

<style lang="scss" scoped>
.pagination {
  @include center-content;
  margin-top: rem(70px);
}

.navigation-button,
.pagination-page {
  @include center-content;
  @include size(rem(40px));
  border: 1px solid $layout-border;
  border-radius: rem(4px);
  transition:
    color $transition,
    background-color $transition,
    border-color $transition;

  &--active {
    color: $global-white;
    background-color: $global-sun-01;
    border-color: $global-sun-01;
  }
}

.pagination-pages {
  display: flex;
  gap: rem(6px);
  justify-content: center;
  margin-inline: rem(6px);
}

.arrow-icon-left {
  transform: scale(-1, 1);
}

.navigation-button:not(.router-link-active) {
  opacity: 0;
}
</style>
